import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { DateSpan } from '../../models/serializable/date-span.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class DateSpanComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'aca080eb34c74fab90f19e48725a271d';
  static readonly SCHEMA_VERSION: number = 3;
  static readonly DEFAULT_RENDER_ORDER: number = 5;

  constructor(
    public readonly dateSpan: DateSpan,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId() {
    return DateSpanComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(DateSpanComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return true;
  }

  static fromMap(context: SerializationContext, map: SerializedData): DateSpanComponent {
    return new DateSpanComponent(
        context.fromData<DateSpan>(MapUtils.get<SerializedData>(map, 'dateSpan')),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'dateSpan': context.toData(this.dateSpan),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new DateSpanComponent(
        this.dateSpan.clone(),
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: DateSpanComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.dateSpan, this.dateSpan) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class DateSpanComponentBuilder extends DataModelBuilder<DateSpanComponent> {
  build(context: SerializationContext, map: SerializedData): DateSpanComponent {
    return DateSpanComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(DateSpanComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(DateSpanComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isPublic', false);
      version = 2;
    }

    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', DateSpanComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
